import { useEffect, useRef, useState } from 'react';
import { MapPin, Hammer, TrendingUp } from 'lucide-react';
import { COMPANY_STATS, NETWORK } from './landingData';

function useCountUp(target, active, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf; const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return val;
}

function Stat({ stat, active }) {
  const n = useCountUp(stat.value, active);
  const shown = stat.raw ? stat.value : n;
  return (
    <div className="glass-dark rounded-2xl p-5 sm:p-6 text-white" data-testid={`tracker-stat-${stat.key}`}>
      <p className="text-4xl sm:text-5xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
        {stat.prefix}{shown}{stat.suffix}
      </p>
      <p className="text-[11px] uppercase tracking-[0.2em] text-orange-300 font-semibold mt-2">{stat.label}</p>
      <p className="text-xs text-white/60 mt-1">{stat.note}</p>
    </div>
  );
}

export const GrowthTracker = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setActive(true), { threshold: 0.3 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  const open = NETWORK.filter(n => n.status === 'open');
  const dev = NETWORK.filter(n => n.status !== 'open');

  return (
    <section id="growth" ref={ref} className="relative bg-[#3E2418] text-white py-20 sm:py-28 px-6 sm:px-10 overflow-hidden grain" data-testid="growth-tracker">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#F26522]/20 blur-3xl" />
      <div className="relative max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.3em] text-orange-300 font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Growth tracker</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              From 1 to 15 restaurants<br /><span className="text-orange-400">in three years.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-white/75 text-base leading-relaxed">
            Ten A&amp;W restaurants open today and five more in development across New Brunswick and Nova Scotia — a network on track for a <strong className="text-white">$32M+ annual run rate</strong> with 220+ team members.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 reveal">
          {COMPANY_STATS.map(s => <Stat key={s.key} stat={s} active={active} />)}
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/60 font-semibold">Network build-out</p>
              <p className="text-xs text-white/60">{open.length} open · {dev.length} in development</p>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden" aria-hidden="true">
              <div className="h-full bg-[#F26522] progress-animate" style={{ width: active ? `${(open.length / NETWORK.length) * 100}%` : '0%' }} />
            </div>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="tracker-open-list">
              {open.map(n => (
                <span key={n.name} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 border border-white/10 rounded-full px-3 py-1.5 text-xs font-medium transition-colors">
                  <MapPin className="w-3 h-3 text-orange-300" />{n.name}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/60 font-semibold mb-3 flex items-center gap-2"><Hammer className="w-3.5 h-3.5" /> In development</p>
            <ul className="divide-y divide-white/10 rounded-2xl border border-white/10 overflow-hidden" data-testid="tracker-dev-list">
              {dev.map(n => (
                <li key={n.name} className="flex items-center justify-between px-4 py-3 bg-white/5 hover:bg-white/10 transition-colors">
                  <div>
                    <p className="text-sm font-semibold">A&amp;W {n.name}</p>
                    <p className="text-xs text-white/55">{n.city}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-amber-300 bg-amber-400/15 rounded-full px-2.5 py-1">Coming soon</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};
